import {isUndefined} from 'lodash';
import {List, Map, OrderedMap} from 'immutable';
import moment from 'moment';

// TODO: TEMPORARY MEASURE.
/*
   no time to change the return value to camel case for the
   mandark specific insight endpoint for inter-assignment guess stats

   transforming here so it works w/ the old table.
*/
export function transformToCamel(questionInsights) {
  return questionInsights.map((insight) => {
    return new Map({
      guessContent: insight.get('guess_content'),
      guessId: insight.get('guess_id'),
      guessTimestamp: insight.get('guess_timestamp'),
      questionAttemptNumber: insight.get('question_attempt_number'),
      questionDifficulty: insight.get('question_difficulty'),
      questionId: insight.get('question_id'),
      questionName: insight.get('question_name'),
      questionOptions: insight.get('question_options'),
      questionCorrect: insight.get('question_correct'),
      questionPointsEarned: insight.get('question_points_earned'),
      questionSetId: insight.get('question_set_id'),
      questionSetSlug: insight.get('question_set_slug'),
      questionTimeElapsed: insight.get('question_time_elapsed'),
      studentId: insight.get('student_id'),
      questionSlug: insight.get('question_slug_id')
    });
  });
}

export function getAttemptLanguage(attemptNumber) {
  if (attemptNumber === 1) {
    return '1st attempt';
  }
  if (attemptNumber === 2) {
    return '2nd attempt';
  }
  if (attemptNumber === 3) {
    return '3rd attempt';
  }
  if (attemptNumber > 3) {
    return `${attemptNumber}th attempt`;
  }
  return undefined;
}

function formatInsightTimestamp(timeStamp) {
  const momentDate = moment(timeStamp);
  return `${momentDate.format('MM-DD-YY')} @ ${momentDate.format('h:mma')}`;
}

function getAttemptFromStudentInsights(questionInsights, attemptNumber) {
  return questionInsights.find((attemptEntry) => {
    return attemptEntry.get('questionAttemptNumber') === attemptNumber;
  }, undefined);
}

export function isAnsweredInStudentInsightEntry(questionInsights) {
  return questionInsights.some((attemptEntry) => {
    return attemptEntry.get('questionAttemptNumber') !== null;
  });
}

function getMostRecentAttempt(questionInsights) {
  /*
    Attempts to find the most recent timestamp in the bunch.
    In the event we only have a single insight entry, we'll first check that
    timestamp as a special case for handling the case where nothing was answered.

    (remember, the endpoint still spits out question info unjoined w/ guesses)
  */
  if (questionInsights.size === 1) {
    const insight = questionInsights.first();
    if (insight.get('questionAttemptNumber')) {
      return insight;
    }
  } else {
    return questionInsights
      .sort((insightEntryLeft, insightEntryRight) => {
        const dateLeft = moment(insightEntryLeft.get('guessTimestamp'));
        const dateRight = moment(insightEntryRight.get('guessTimestamp'));

        // the equal case is probably not important here. If there's a tie in times
        // which there never should be, we can move whichever one to the front.
        return dateRight.isSameOrBefore(dateLeft) ? -1 : 1;
      })
      .first();
  }
  return null;
}

function getMultipleChoiceAnswerValueFromAttempt(attempt) {
  /*
    Below I am pulling out the key/values in the guessContent map that have `true` as their value.

    The resulting map will be used to pull values out of the available options for the question.
    Because we want to show what the user selected, we take the key from `guessContent` and use it
    to index into the question options.

    We are building a list because potentially a user can select more than one thing in certain cases.
  */
  const attemptQuestionOptions = attempt.get('questionOptions', new List());
  // If we don't have an options list that has stuff in it
  // then this probably is not a multiple choice question and we should bail.
  if (attemptQuestionOptions.isEmpty()) {
    return List.of('n/a');
  }
  const attemptGuessContent = attempt.get('guessContent', new Map()) || new Map();
  const multipleChoiceAnswers = new List(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']);

  const guessContentFiltered = attemptGuessContent.filter((content, key) => {
    return attemptGuessContent.get(key, false) === true;
  });
  const attemptAnswers = attemptQuestionOptions.reduce((acc, option, index) => {
    if (guessContentFiltered.has(option.get('id'))) {
      return acc.push(multipleChoiceAnswers.get(index));
    }
    return acc;
  }, new List());

  return attemptAnswers;
}

export function getAttemptTimestampStringForStudentInsightEntry(insightEntry, attemptNumber) {
  const answerForAttempt = getAttemptFromStudentInsights(insightEntry, attemptNumber);
  if (answerForAttempt) {
    const dateString = answerForAttempt.get('guessTimestamp');
    return formatInsightTimestamp(dateString);
  }

  /*
    if we don't have an attempt for the selected number, it is reasonable to assume that the number
    of entries in the list at `questionInsightObject` represents the # of attempts.

    If the # of entries in the list is ONE, we need to check the attempt number on that object, to make sure
    it is not nil. If it is nil, there is no guess for that question. The mandark response does a left join on
    guesses there.
  */
  if (isAnsweredInStudentInsightEntry(insightEntry)) {
    const {size} = insightEntry;
    return size === 1 ? `${size} Attempt Only` : `${size} Attempts Only`;
  }

  return 'No Answer';
}

export function isAnsweredForAttemptInStudentInsightEntry(insightEntry, attemptNumber) {
  const attempt = getAttemptFromStudentInsights(insightEntry, attemptNumber);
  return !isUndefined(attempt);
}

export function isCorrectForAttemptInStudentInsightEntry(insightEntry, attemptNumber) {
  const answerForAttempt = getAttemptFromStudentInsights(insightEntry, attemptNumber);
  if (!answerForAttempt) {
    return false;
  }

  return answerForAttempt.get('questionCorrect') !== false;
}

export function getDurationForAttemptInStudentInsightEntry(insightEntry, attemptNumber) {
  const answerForAttempt = getAttemptFromStudentInsights(insightEntry, attemptNumber);
  if (!answerForAttempt) {
    return 'n/a';
  }
  // Math time, friends.
  // If we're > 20 minutes -- we are showing that. Otherwise show minutes. If we are sub-1 minute, show seconds.
  const questionTimeElapsed = parseInt(answerForAttempt.get('questionTimeElapsed'), 10);
  const momentDuration = moment.duration(questionTimeElapsed);

  const minutes = momentDuration.asMinutes();
  const flooredMinutes = Math.floor(minutes);
  if (flooredMinutes === 0) {
    return `${Math.floor(momentDuration.asSeconds())}s`;
  }
  if (flooredMinutes <= 20) {
    return `${flooredMinutes}m`;
  }
  return '>20m';
}

export function getDifficultyForQuestion(insightEntry) {
  /*
    We can take the first entry in our insights list and grab the difficulty.
    Remember there, is always a single item in this list - even if there were no guesses it got joined againsr
  */
  return insightEntry.first().get('questionDifficulty');
}

export function getGuessValueForAttempt(insightEntry, attemptNumber) {
  const attempt = getAttemptFromStudentInsights(insightEntry, attemptNumber);
  if (!attempt) {
    return List.of('n/a');
  }
  return getMultipleChoiceAnswerValueFromAttempt(attempt);
}

export function getRecentAttemptTimestampString(insightEntry) {
  /*
    Attempts to find the most recent timestamp in the bunch.
    In the event we only have a single insight entry, we'll first check that
    timestamp as a special case for handling the case where nothing was answered.

    (remember, the endpoint still spits out question info unjoined w/ guesses)
  */
  const mostRecentAttempt = getMostRecentAttempt(insightEntry);

  if (mostRecentAttempt) {
    return formatInsightTimestamp(mostRecentAttempt.get('guessTimestamp'));
  }

  return 'No Answer';
}

export function getAttemptList(insightEntry) {
  /*
    Returns an easy to use object of attemptData as a list in order
    to populate the attempt badges and their tooltips for the
    no-attempt view of the insights.
  */
  if (insightEntry.size === 1) {
    const insight = insightEntry.first();
    if (!insight.get('questionAttemptNumber')) {
      return List.of(
        new Map({
          attemptNumber: '',
          duration: '',
          timestamp: '',
          isCorrect: false,
          isAnswered: false,
          guessId: undefined
        })
      );
    }
  }

  /*
    Sort in order of attempt timestamps starting with the oldest.

    We provide this sorted list to bne then made into badges.
  */
  const sortedInsights = insightEntry.sort((insightDataLeft, insightDataRight) => {
    const dateLeft = moment(insightDataLeft.get('guessTimestamp'));
    const dateRight = moment(insightDataRight.get('guessTimestamp'));
    return dateLeft.isSameOrBefore(dateRight) ? -1 : 1;
  });

  // Always execute this block if the single-insight result HAS an attempt
  const attemptList = sortedInsights.reduce((acc, entry) => {
    const attemptNumber = entry.get('questionAttemptNumber');
    return acc.push(
      new Map({
        attemptNumber,
        duration: getDurationForAttemptInStudentInsightEntry(insightEntry, attemptNumber),
        timestamp: formatInsightTimestamp(entry.get('guessTimestamp')),
        isCorrect: isCorrectForAttemptInStudentInsightEntry(insightEntry, attemptNumber),
        isAnswered: isAnsweredForAttemptInStudentInsightEntry(insightEntry, attemptNumber),
        guessId: entry.get('guessId')
      })
    );
  }, new List());

  return attemptList;
}

export function getMostRecentGuessValue(insightEntry) {
  const hasBeenAnswered = isAnsweredInStudentInsightEntry(insightEntry);
  if (!hasBeenAnswered) {
    return List.of('n/a');
  }
  const mostRecentAttempt = getMostRecentAttempt(insightEntry);
  return getMultipleChoiceAnswerValueFromAttempt(mostRecentAttempt);
}

export function getNumberOfAnsweredQuestionsInStudentInsights(insights) {
  // Summation of answers across all attempts  - not a single one.
  return insights.reduce((acc, insightEntry) => {
    if (isAnsweredInStudentInsightEntry(insightEntry)) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export function getNumberOfAnsweredQuestionsInStudentInsightsForAttempt(insights, attemptNumber) {
  // Summation of answers across all attempts  - not a single one.
  return insights.reduce((acc, insightEntry) => {
    if (isAnsweredForAttemptInStudentInsightEntry(insightEntry, attemptNumber)) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export function getTotalNumberOfQuestionsInStudentInsights(insights) {
  return insights.keySeq().size;
}

// TODO: SORT BY IS TEMPORARY
// DUE TO THE CAMEL CASE / SNAKE CASE DIFFERENCE IN RESPONSES
export function getSortedInsights(insights, sortBy = 'questionId') {
  const answeredInsights = insights.filter((insightEntry) => {
    return isAnsweredInStudentInsightEntry(insightEntry);
  });
  const unansweredInsights = insights.filter((insightEntry) => {
    return !isAnsweredInStudentInsightEntry(insightEntry);
  });

  const idSort = (lhs, rhs) => {
    const lhsId = lhs.first().get(sortBy);
    const rhsId = rhs.first().get(sortBy);
    return lhsId.localeCompare(rhsId);
  };

  return new OrderedMap().concat(answeredInsights.sort(idSort), unansweredInsights.sort(idSort));
}

export function sortBy(insights, sortMethod) {
  return new OrderedMap().concat(insights.sort(sortMethod));
}
